import { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

interface Client {
  id: number
  name: string
  address?: string
  contact_person?: string
  phone?: string
  email?: string
}

interface ClientContextSelectorProps {
  token: string
  onContextChange?: (clientId: number | null, clientName: string | null) => void
}

export default function ClientContextSelector({ token, onContextChange }: ClientContextSelectorProps) {
  const [clients, setClients] = useState<Client[]>([])
  const [currentContext, setCurrentContext] = useState<{ client_id: number | null, client_name: string | null }>({
    client_id: null,
    client_name: null
  })
  const [loading, setLoading] = useState(true)
  const { showError } = useNotificationContext()

  useEffect(() => {
    fetchUserClients()
    fetchCurrentContext()
  }, [])

  const fetchUserClients = async () => {
    try {
      console.log('Pobieranie referatów użytkownika...')
      const response = await fetch('/api/users/me/clients', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      console.log('Odpowiedź z /api/users/me/clients:', response.status, response.statusText)

      if (response.ok) {
        const data = await response.json()
        console.log('Dane referatów użytkownika:', data)
        setClients(data)
      } else {
        const errorText = await response.text()
        console.error('Błąd podczas ładowania referatów:', response.status, errorText)
        showError('Błąd podczas ładowania referatów')
      }
    } catch (error) {
      console.error('Błąd połączenia z serwerem podczas ładowania referatów:', error)
      showError('Błąd połączenia z serwerem')
    }
  }

  const fetchCurrentContext = async () => {
    try {
      const response = await fetch('/api/users/me/context', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setCurrentContext(data)
        if (onContextChange) {
          onContextChange(data.client_id, data.client_name)
        }
      }
    } catch (error) {
      console.error('Błąd podczas pobierania kontekstu:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleContextChange = async (clientId: number) => {
    try {
      const response = await fetch('/api/users/me/context', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_id: clientId }),
      })

      if (response.ok) {
        const selectedClient = clients.find(c => c.id === clientId)
        const newContext = {
          client_id: clientId,
          client_name: selectedClient?.name || null
        }
        setCurrentContext(newContext)
        
        if (onContextChange) {
          onContextChange(newContext.client_id, newContext.client_name)
        }
      } else {
        showError('Błąd podczas zmiany kontekstu')
      }
    } catch (error) {
      showError('Błąd połączenia z serwerem')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Ładowanie...</span>
      </div>
    )
  }

  if (clients.length === 0) {
    return (
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Brak przypisanych referatów</span>
      </div>
    )
  }

  if (clients.length === 1) {
    return (
      <div className="flex items-center space-x-2 text-sm">
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <span className="font-medium text-gray-900">{clients[0].name}</span>
      </div>
    )
  }

  return (
    <div className="flex items-center space-x-2">
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      <select
        value={currentContext.client_id || ''}
        onChange={(e) => handleContextChange(parseInt(e.target.value))}
        className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Wybierz referat</option>
        {clients.map((client) => (
          <option key={client.id} value={client.id}>
            {client.name}
          </option>
        ))}
      </select>
    </div>
  )
}
